import React, { useEffect, useRef, useState } from "react";
import {
  FaHeart,
  FaHandHoldingHeart,
  FaUsers,
  FaUserMd,
  FaGift,
  FaCheckCircle,
  FaStar,
  FaQuoteLeft,
  FaCoins,
  FaShieldAlt,
  FaTimes,
  FaDownload,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styles from "./DonatePage.module.css";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import ScrollToTop from "../../components/ScrollToTop/ScrollToTop";
import DonationPayment from "../../components/DonationPayment/DonationPayment";
import { generateDonationReceipt } from "../../utils/receiptGenerator";
import { ROUTES } from "../../constants/routes";

interface DonationSuccess {
  amount: number;
  donorName: string;
  donorEmail: string;
  isAnonymous: boolean;
  transactionId: string;
  date: string;
}

const DonatePage: React.FC = () => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const formSectionRef = useRef<HTMLElement>(null);
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState<string>("");
  const [donorInfo, setDonorInfo] = useState({
    name: "",
    email: "",
    anonymous: false,
  });
  const [donationSuccess, setDonationSuccess] =
    useState<DonationSuccess | null>(null);
  const [showSuccessSection, setShowSuccessSection] = useState(false);
  const [celebrationActive, setCelebrationActive] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  useEffect(() => {
    // Initialize scroll reveal animations
    const observerOptions = {
      threshold: 0.2,
      rootMargin: "0px 0px -20px 0px",
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("revealed");
        }
      });
    }, observerOptions);

    // Observe all scroll reveal elements
    const elementsToObserve = document.querySelectorAll(
      ".scroll-reveal, .scroll-reveal-left, .scroll-reveal-right, .scroll-reveal-scale"
    );

    elementsToObserve.forEach((el) => {
      if (observerRef.current) {
        observerRef.current.observe(el);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const donationOptions = [
    { amount: 1000, impact: "Helps 1 patient with consultation fees" },
    { amount: 2500, impact: "Covers basic medication for a family" },
    { amount: 5000, impact: "Sponsors a complete health checkup" },
    { amount: 10000, impact: "Funds emergency medical treatment" },
    { amount: 25000, impact: "Supports specialist consultation & tests" },
    { amount: 50000, impact: "Covers surgery costs for one patient" },
  ];

  const impactStats = [
    {
      icon: <FaUsers />,
      number: "20,000+",
      label: "Patients Helped",
      description: "Through community donations",
    },
    {
      icon: <FaUserMd />,
      number: "800+",
      label: "Healthcare Providers",
      description: "Supporting the cause",
    },
    {
      icon: <FaCoins />,
      number: "₦50M+",
      label: "Total Donations",
      description: "Raised for medical aid",
    },
    {
      icon: <FaHeart />,
      number: "98%",
      label: "Success Rate",
      description: "In emergency cases",
    },
  ];

  const testimonials = [
    {
      name: "Sarah Okafor",
      location: "Lagos, Nigeria",
      image:
        "https://images.unsplash.com/photo-1494790108755-2616b612b830?w=60&h=60&fit=crop&crop=face",
      quote:
        "Thanks to LicoMeds community support, I was able to get the surgery my daughter needed. The platform made it so easy to request help and receive donations from caring people.",
      rating: 5,
    },
    {
      name: "Dr. Michael Adebayo",
      location: "Abuja, Nigeria",
      image:
        "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=60&h=60&fit=crop&crop=face",
      quote:
        "As a healthcare provider on LicoMeds, I've seen firsthand how donations change lives. The community support feature is revolutionary for healthcare accessibility in Africa.",
      rating: 5,
    },
    {
      name: "Grace Emeka",
      location: "Port Harcourt, Nigeria",
      image:
        "https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=60&h=60&fit=crop&crop=face",
      quote:
        "I donated through LicoMeds to help other patients, and seeing the impact reports makes me feel proud. It's amazing how small contributions make huge differences.",
      rating: 5,
    },
  ];

  const handleAmountSelect = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount(amount.toLocaleString("en-NG"));
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, "");

    if (value) {
      const numericValue = parseInt(value);
      const formattedValue = numericValue.toLocaleString("en-NG");
      setCustomAmount(formattedValue);
      setSelectedAmount(numericValue);
    } else {
      setCustomAmount("");
      setSelectedAmount(null);
    }
  };

  const handleDonorInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setDonorInfo((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Scroll to form section with smooth animation
  const scrollToFormSection = () => {
    if (formSectionRef.current) {
      formSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // Handle donation success
  const handleDonationSuccess = (transactionId: string) => {
    if (selectedAmount) {
      const successData: DonationSuccess = {
        amount: selectedAmount,
        donorName: donorInfo.name || "Anonymous",
        donorEmail: donorInfo.email,
        isAnonymous: donorInfo.anonymous,
        transactionId,
        date: new Date().toISOString(),
      };

      setDonationSuccess(successData);
      setShowSuccessSection(true);
      setCelebrationActive(true);

      // Scroll to top of form section to show success
      setTimeout(() => {
        scrollToFormSection();

        // Trigger celebration animation
        const celebrationElement = document.querySelector(
          `.${styles.celebrationAnimation}`
        );
        if (celebrationElement) {
          celebrationElement.classList.add(styles.animate);
        }
      }, 100);
    }
  };

  const handleCloseSuccess = () => {
    setShowCloseConfirm(true);
  };

  const confirmCloseSuccess = () => {
    setShowSuccessSection(false);
    setDonationSuccess(null);
    setCelebrationActive(false);
    setShowCloseConfirm(false);

    // Remove animation class
    const celebrationElement = document.querySelector(
      `.${styles.celebrationAnimation}`
    );
    if (celebrationElement) {
      celebrationElement.classList.remove(styles.animate);
    }
  };

  const cancelCloseSuccess = () => {
    setShowCloseConfirm(false);
  };

  const downloadReceipt = async () => {
    if (!donationSuccess) {
      toast.error("No donation data available for receipt generation.");
      return;
    }

    try {
      toast.info("Generating your receipt...", { autoClose: 2000 });

      await generateDonationReceipt({
        amount: donationSuccess.amount,
        donorName: donationSuccess.donorName,
        donorEmail: donationSuccess.donorEmail,
        isAnonymous: donationSuccess.isAnonymous,
        transactionId: donationSuccess.transactionId,
        date: donationSuccess.date,
      });

      toast.success("Receipt downloaded successfully!");
    } catch (error) {
      console.error("Receipt generation error:", error);
      toast.error(
        "Failed to generate receipt. Please try again or contact support."
      );
    }
  };

  return (
    <>
      <div className={styles.donatePage}>
        <Navbar />
        <ScrollToTop />

        {/* Toast Container */}
        <ToastContainer
          position="top-center"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />

        {/* Hero Section */}
        <section className={styles.hero}>
          <div className={styles.container}>
            <div className={styles.heroContent}>
              <div className={styles.heroBadge}>
                <FaHeart className={styles.badgeIcon} />
                Community Support
              </div>

              <h1 className={styles.heroTitle}>
                Make Healthcare
                <span className={styles.heroAccent}> Accessible</span>
              </h1>

              <p className={styles.heroSubtitle}>
                Join our mission to provide quality healthcare for everyone.
                Your donation directly helps patients access medical care and
                life-saving treatments through {ROUTES.COMPANY.NAME}.
              </p>

              <div className={styles.heroVisual}>
                <div
                  className={`${styles.heroImageContainer} ${styles.floatGentle}`}
                >
                  <div className={`${styles.heroIcon} ${styles.heartbeatFlow}`}>
                    <FaHandHoldingHeart />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Impact Stats */}
        <section className={styles.impactStats}>
          <div className={styles.container}>
            <div className={`${styles.statsGrid} scroll-reveal`}>
              {impactStats.map((stat, index) => (
                <div key={index} className={styles.statCard}>
                  <div className={styles.statIcon}>{stat.icon}</div>
                  <div className={styles.statNumber}>{stat.number}</div>
                  <div className={styles.statLabel}>{stat.label}</div>
                  <div className={styles.statDescription}>
                    {stat.description}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Donation Form Section */}
        <section ref={formSectionRef} className={styles.donationForm}>
          <div className={styles.container}>
            <div className={`${styles.formContainer} scroll-reveal`}>
              {/* Show Success Section or Donation Form */}
              {showSuccessSection && donationSuccess ? (
                // Donation Success Section
                <div className={styles.successSection}>
                  {/* Confirmation Dialog */}
                  {showCloseConfirm && (
                    <div className={styles.confirmationOverlay}>
                      <div className={styles.confirmationDialog}>
                        <div className={styles.confirmationIcon}>
                          <FaHeart />
                        </div>
                        <h3 className={styles.confirmationTitle}>
                          Are you sure?
                        </h3>
                        <p className={styles.confirmationMessage}>
                          If you close this receipt, you'll need to download it
                          now or it will be lost. Make sure to save your receipt
                          for your records.
                        </p>
                        <div className={styles.confirmationActions}>
                          <button
                            className={`${styles.confirmBtn} ${styles.download}`}
                            onClick={downloadReceipt}
                          >
                            <FaDownload />
                            Download & Close
                          </button>
                          <button
                            className={`${styles.confirmBtn} ${styles.close}`}
                            onClick={confirmCloseSuccess}
                          >
                            Close Without Download
                          </button>
                          <button
                            className={`${styles.confirmBtn} ${styles.cancel}`}
                            onClick={cancelCloseSuccess}
                          >
                            Keep Receipt Open
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Celebration Animation */}
                  <div
                    className={`${styles.celebrationAnimation} ${
                      celebrationActive ? styles.animate : ""
                    }`}
                  >
                    <div className={styles.balloon}></div>
                    <div className={styles.balloon}></div>
                    <div className={styles.balloon}></div>
                    <div className={styles.confetti}></div>
                    <div className={styles.confetti}></div>
                    <div className={styles.confetti}></div>
                    <div className={styles.confetti}></div>
                    <div className={styles.confetti}></div>
                  </div>

                  {/* Close Button */}
                  <button
                    className={styles.closeButton}
                    onClick={handleCloseSuccess}
                    aria-label="Close success section"
                  >
                    <FaTimes />
                  </button>

                  {/* Success Header */}
                  <div className={styles.successHeader}>
                    <div className={styles.successIcon}>
                      <FaCheckCircle />
                    </div>
                    <h2 className={styles.successTitle}>
                      Donation Successful!
                    </h2>
                    <p className={styles.successSubtitle}>
                      Thank you for your generous contribution to healthcare accessibility
                    </p>

                    {/* Primary Download Button - Prominent Position */}
                    <div className={`${styles.successActions} mt-3`}>
                      <button
                        className={`${styles.actionBtn} ${styles.primary}`}
                        onClick={downloadReceipt}
                      >
                        <FaDownload />
                        Download Receipt
                      </button>
                    </div>
                  </div>

                  {/* Donation Details */}
                  <div className={styles.donationDetails}>
                    <div className={styles.detailCard}>
                      <h3>Donation Information</h3>
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Amount:</span>
                        <span className={styles.detailValue}>
                          {formatAmount(donationSuccess.amount)}
                        </span>
                      </div>
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Donor:</span>
                        <span className={styles.detailValue}>
                          {donationSuccess.isAnonymous
                            ? "Anonymous"
                            : donationSuccess.donorName}
                        </span>
                      </div>
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Email:</span>
                        <span className={styles.detailValue}>
                          {donationSuccess.donorEmail}
                        </span>
                      </div>
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>
                          Transaction ID:
                        </span>
                        <span className={styles.detailValue}>
                          {donationSuccess.transactionId}
                        </span>
                      </div>
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Date:</span>
                        <span className={styles.detailValue}>
                          {new Date(donationSuccess.date).toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    {/* Impact Information */}
                    <div className={styles.impactCard}>
                      <h3>Your Impact</h3>
                      <div className={styles.impactInfo}>
                        <FaHeart className={styles.impactIcon} />
                        <p>
                          {donationOptions.find(
                            (opt) => opt.amount === donationSuccess.amount
                          )?.impact ||
                            "Your custom donation will support healthcare accessibility for those in need."}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons - Secondary Download Option */}
                  <div className={styles.successActions}>
                    <button
                      className={`${styles.actionBtn} ${styles.primary}`}
                      onClick={downloadReceipt}
                    >
                      <FaDownload />
                      Download Another Copy
                    </button>
                  </div>

                  {/* Thank You Message */}
                  <div className={styles.thankYouMessage}>
                    <p>
                      Your donation will be used to provide medical care, fund
                      treatments, and support healthcare initiatives that save
                      lives. Together, we're making healthcare accessible to
                      everyone who needs it.
                    </p>
                  </div>
                </div>
              ) : (
                // Regular Donation Form
                <>
                  <div className={styles.formHeader}>
                    <div className={styles.formIcon}>
                      <FaGift />
                    </div>
                    <h2 className={styles.formTitle}>Make Your Donation</h2>
                    <p className={styles.formSubtitle}>
                      Choose an amount and provide your details for a secure
                      donation experience.
                    </p>
                  </div>

                  {/* Amount Selection */}
                  <div className={styles.amountSelection}>
                    <h3 className={styles.sectionLabel}>Select Amount</h3>
                    <div className={styles.amountGrid}>
                      {donationOptions.map((option, index) => (
                        <button
                          key={index}
                          className={`${styles.amountOption} ${
                            selectedAmount === option.amount
                              ? styles.selected
                              : ""
                          }`}
                          onClick={() => handleAmountSelect(option.amount)}
                        >
                          <span className={styles.amountValue}>
                            {formatAmount(option.amount)}
                          </span>
                          <span className={styles.amountImpact}>
                            {option.impact}
                          </span>
                        </button>
                      ))}
                    </div>

                    <div className={styles.customAmount}>
                      <label className={styles.customLabel}>
                        Or enter custom amount (₦)
                      </label>
                      <input
                        type="text"
                        value={customAmount}
                        onChange={handleCustomAmountChange}
                        placeholder="Enter amount"
                        className={styles.customInput}
                      />
                    </div>
                  </div>

                  {/* Donor Information */}
                  <div className={styles.donorInfo}>
                    <h3 className={styles.sectionLabel}>Your Information</h3>
                    <div className={styles.infoGrid}>
                      <div className={styles.inputGroup}>
                        <label htmlFor="name">Name (Optional)</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={donorInfo.name}
                          onChange={handleDonorInfoChange}
                          placeholder="Your name"
                          className={styles.infoInput}
                        />
                      </div>
                      <div className={styles.inputGroup}>
                        <label htmlFor="email">Email Address</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={donorInfo.email}
                          onChange={handleDonorInfoChange}
                          placeholder="your@email.com"
                          className={styles.infoInput}
                          required
                        />
                      </div>
                    </div>
                    <div className={styles.checkboxGroup}>
                      <input
                        type="checkbox"
                        id="anonymous"
                        name="anonymous"
                        checked={donorInfo.anonymous}
                        onChange={handleDonorInfoChange}
                        className={styles.checkbox}
                      />
                      <label htmlFor="anonymous">
                        Make this donation anonymous
                      </label>
                    </div>
                  </div>

                  {/* Donation Summary */}
                  {selectedAmount && (
                    <div className={styles.donationSummary}>
                      <div className={styles.summaryAmount}>
                        Donation: {formatAmount(selectedAmount)}
                      </div>
                      <div className={styles.summaryImpact}>
                        {donationOptions.find(
                          (opt) => opt.amount === selectedAmount
                        )?.impact ||
                          "Custom donation to support healthcare accessibility"}
                      </div>
                    </div>
                  )}

                  {/* Payment Button */}
                  <div className={styles.donateButtonContainer}>
                    <DonationPayment
                      amount={(selectedAmount || 0) * 100}
                      email={donorInfo.email || "donor@example.com"}
                      name={donorInfo.name}
                      anonymous={donorInfo.anonymous}
                      className={styles.primaryDonateButton}
                      onSuccess={handleDonationSuccess}
                    />
                  </div>

                  <div className={styles.securityNote}>
                    <FaShieldAlt />
                    <span>Secure payment powered by Paystack</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>

        {/* Stories of Impact - Testimonials */}
        <section className={styles.testimonials}>
          <div className={styles.container}>
            <div className={`${styles.testimonialsContent} scroll-reveal`}>
              <h2 className={styles.testimonialsTitle}>Stories of Impact</h2>
              <p className={styles.testimonialsSubtitle}>
                Real stories from patients and healthcare providers who have
                experienced the power of community support.
              </p>

              <div className={styles.testimonialsGrid}>
                {testimonials.map((testimonial, index) => (
                  <div key={index} className={styles.testimonialCard}>
                    <div className={styles.testimonialQuote}>
                      <FaQuoteLeft className={styles.quoteIcon} />
                    </div>
                    <p className={styles.testimonialText}>
                      {testimonial.quote}
                    </p>
                    <div className={styles.testimonialRating}>
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <FaStar key={i} className={styles.starIcon} />
                      ))}
                    </div>
                    <div className={styles.testimonialAuthor}>
                      <img
                        src={testimonial.image}
                        alt={testimonial.name}
                        className={styles.authorImage}
                        onError={(e) => {
                          (
                            e.target as HTMLImageElement
                          ).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                            testimonial.name
                          )}&background=random&size=45`;
                        }}
                      />
                      <div>
                        <div className={styles.authorName}>
                          {testimonial.name}
                        </div>
                        <div className={styles.authorLocation}>
                          {testimonial.location}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default DonatePage;
